import React, { useEffect, useMemo, useState } from "react";
import { client } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export const EQUIPMENT_STATUS_OPTIONS = [
  { value: "", label: "Not recorded" },
  { value: "running", label: "Running" },
  { value: "running-monitor", label: "Running — monitor" },
  { value: "out_of_service", label: "Out of service" },
];
const SELECT = "w-full h-9 rounded-[10px] px-2 text-[13.5px] bg-white";
const BLANK = { make: "", model: "", serial_number: "", install_date: "", status: "", access_notes: "" };

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Picker({ label, value, onChange, options, testid, placeholder, className }) {
  return (
    <Field label={label} className={className}>
      <select className={SELECT} style={{ border: "1px solid #E7EAEE" }} value={value}
              onChange={e => onChange(e.target.value)} data-testid={testid}>
        <option value="">{placeholder}</option>
        {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </Field>
  );
}

function ExtraFields({ spec, values, setValue }) {
  if (!spec.length) return null;
  return spec.map(field => (
    <Field key={field.label} label={field.label}>
      {(field.options || []).length ? (
        <select className={SELECT} style={{ border: "1px solid #E7EAEE" }} value={values[field.label] || ""}
                onChange={e => setValue(field.label, e.target.value)} data-testid={`add-equipment-extra-${field.label}`}>
          <option value="">—</option>
          {field.options.map(option => <option key={option} value={option}>{option}</option>)}
        </select>
      ) : (
        <Input className="rounded-[10px]" value={values[field.label] || ""}
               onChange={e => setValue(field.label, e.target.value)} data-testid={`add-equipment-extra-${field.label}`} />
      )}
    </Field>
  ));
}

function useRegister(open) {
  const [data, setData] = useState({ customers: [], sites: [], areas: [], types: [] });
  useEffect(() => {
    if (!open) return;
    Promise.all([client.get("/customers"), client.get("/sites"), client.get("/areas")])
      .then(([c, s, a]) => setData(d => ({ ...d, customers: c.data, sites: s.data, areas: a.data })))
      .catch(() => toast.error("Could not load customers, sites and areas"));
    client.get("/settings/equipment-types")
      .then(t => setData(d => ({ ...d, types: t.data.filter(type => type.active !== false) })))
      .catch(() => toast.error("Could not load the equipment types"));
  }, [open]);
  return data;
}

export function AddEquipmentDialog({ open, onOpenChange, preset = {}, onSaved }) {
  const register = useRegister(open);
  const [customerId, setCustomerId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [areaId, setAreaId] = useState("");
  const [type, setType] = useState("");
  const [form, setForm] = useState(BLANK);
  const [extras, setExtras] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCustomerId(preset.customerId || ""); setSiteId(preset.siteId || ""); setAreaId(preset.areaId || "");
    setType(""); setForm(BLANK); setExtras({}); setError("");
  }, [open, preset.customerId, preset.siteId, preset.areaId]);

  const sites = useMemo(() => register.sites.filter(s => s.customer_id === customerId), [register.sites, customerId]);
  const areas = useMemo(() => register.areas.filter(a => a.site_id === siteId), [register.areas, siteId]);
  const spec = register.types.find(t => t.name === type)?.custom_fields || [];
  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));

  const save = async () => {
    setSaving(true); setError("");
    try {
      const custom_fields = spec.map(field => ({ label: field.label, value: extras[field.label] || "" }));
      await client.post("/equipment", { ...form, area_id: areaId, type, custom_fields });
      toast.success("Equipment added");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not add the equipment");
    } finally { setSaving(false); }
  };

  const text = (label, key, testid, extra = {}) => (
    <Field label={label}>
      <Input className="rounded-[10px]" value={form[key]} onChange={e => set(key, e.target.value)} data-testid={testid} {...extra} />
    </Field>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px] max-h-[88vh] overflow-y-auto" data-testid="add-equipment-dialog">
        <DialogHeader>
          <DialogTitle>Add equipment</DialogTitle>
          <DialogDescription>Customer, site, area and type are required.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Picker label="Customer" value={customerId} placeholder="Choose customer" testid="add-equipment-customer"
                  options={register.customers.map(c => ({ value: c.id, label: c.name }))}
                  onChange={v => { setCustomerId(v); setSiteId(""); setAreaId(""); }} />
          <Picker label="Site" value={siteId} placeholder="Choose site" testid="add-equipment-site"
                  options={sites.map(s => ({ value: s.id, label: s.name }))}
                  onChange={v => { setSiteId(v); setAreaId(""); }} />
          <Picker label="Area" value={areaId} placeholder="Choose area" testid="add-equipment-area"
                  options={areas.map(a => ({ value: a.id, label: a.name }))} onChange={setAreaId} />
          <Picker label="Equipment type" value={type} placeholder="Choose type" testid="add-equipment-type"
                  options={register.types.map(t => ({ value: t.name, label: t.name }))}
                  onChange={v => { setType(v); setExtras({}); }} />
          {text("Make", "make", "add-equipment-make")}
          {text("Model", "model", "add-equipment-model")}
          {text("Serial number", "serial_number", "add-equipment-serial", { placeholder: "Leave blank if none" })}
          {text("Install date", "install_date", "add-equipment-install-date", { type: "date" })}
          <Picker label="Status" value={form.status} placeholder="Not recorded" testid="add-equipment-status"
                  options={EQUIPMENT_STATUS_OPTIONS.slice(1)} onChange={v => set("status", v)} />
          {text("Access notes", "access_notes", "add-equipment-access-notes")}
          <ExtraFields spec={spec} values={extras} setValue={(label, value) => setExtras(c => ({ ...c, [label]: value }))} />
        </div>
        {error && <div className="text-[12.5px] mt-2" style={{ color: "#B42318" }} data-testid="add-equipment-error">{error}</div>}
        <DialogFooter className="mt-3">
          <Button variant="outline" className="bf-btn-ghost" onClick={() => onOpenChange(false)} data-testid="add-equipment-cancel">Cancel</Button>
          <Button className="bf-btn-primary px-4" disabled={saving || !areaId || !type} onClick={save} data-testid="add-equipment-save">
            {saving ? "Saving…" : "Add equipment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
